import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import fillMonths from 'teacher-ui-client/utils/student-events/fill-months'

export interface StadiumEventsTotalsResponse {
  stadium_events_totals_summary_by_grade: SummaryData[]
  stadium_events_totals_by_grade_by_month: TotalsData[]
}

interface SummaryData {
  grade_position: number
  data: [
    { category: 'total_count'; count: number },
    { category: 'student_count'; count: number },
    { category: 'student_average'; average: number },
  ]
}

interface TotalsData {
  grade_position: number
  months: {
    month: string
    spelling?: number
    grammar?: number
    usage?: number
    vocabulary?: number
  }[]
}

export interface StadiumEventsTotalsDataItem {
  grade_position: number
  summary: {
    total_count: number
    student_count: number
    student_average: number
  }
  months: {
    month: string
    spelling: number
    grammar: number
    usage: number
    vocabulary: number
  }[]
}

export async function stadiumEventsTotals(
  this: StudentEventsService,
  product: LegacyProductRex,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<StadiumEventsTotalsDataItem[]>> {
  if (isEmpty(statsScope.ids)) return Promise.resolve(null)

  const url = this.buildUrl('stadium_events_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<StadiumEventsTotalsResponse>(url, query)
  if (!data) return null

  const { stadium_events_totals_summary_by_grade: summaryData, stadium_events_totals_by_grade_by_month: totalsData } =
    data

  if (totalsData.length === 0) return null

  return groupByGrade(summaryData, totalsData, period)
}

function groupByGrade(summaryData: SummaryData[], totalsData: TotalsData[], period: NormalizedInterval) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const totals = totalsData.find((total) => total.grade_position === summary.grade_position)
      if (!totals) return null

      const total_count = summary.data.find((summaryItem) => summaryItem.category === 'total_count')!.count
      const student_count = summary.data.find((summaryItem) => summaryItem.category === 'student_count')!.count
      const student_average = summary.data.find((summaryItem) => summaryItem.category === 'student_average')!.average

      const parsedMonthData = totals.months.map((month) => ({
        spelling: 0,
        grammar: 0,
        vocabulary: 0,
        usage: 0,
        ...month,
      }))
      const filledMonths = fillMonths(parsedMonthData, period, {
        spelling: 0,
        grammar: 0,
        vocabulary: 0,
        usage: 0,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          total_count,
          student_count,
          student_average,
        },
        months: filledMonths,
      }
    })
    .filter((data) => data !== null)
}
